import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactElement } from "react";
import type { DueItem, TicketBoard, TicketBoardCard, Unternehmen } from "@kundeninfo/api-client";
import { Icon, useSofortPopup, useTelefon } from "@kundeninfo/ui";
import { Chip, Hinweis, Knopf, Optionen, Werkzeugleiste } from "@kundeninfo/ui/me-design";
import { useDiagrammThema } from "@kundeninfo/ui/diagramm";
import { KiKnopf } from "../../components/meHuellen.js";
import { keepInView } from "../../scroll.js";
import { TicketFilterleiste, anzahlAktiverFilter, filterAnfrage } from "./TicketFilterleiste.js";
import { LEERER_FILTER, merkeFilter } from "./boardSpeicher.js";
import type { BoardFilter } from "./boardSpeicher.js";
import { msBisMitternachtBerlin } from "./TicketBoard.js";
import { TicketTimelineChart } from "./TicketTimelineChart.js";
import { TicketTimelineRows } from "./TicketTimelineRows.js";
import type { TimelineSelection } from "./TicketTimelineRows.js";
import {
  addDays, buildDueGroups, buildTimeline, HEADER_PX, LABEL_PX, LABEL_PX_PHONE, layout, nearestStage, nowOnAxis, shiftWindow,
  stageOf, TIMELINE_STAGES, windowFor,
} from "./timelineModel.js";
import type {
  TicketTimelineRows as TimelineRowsData, TimelineGroup, TimelinePerson, TimelineStage, TimelineWindow,
} from "./timelineModel.js";
import { useDueItems } from "./useDueItems.js";
import { readCollapsed, readStage, rememberCollapsed, rememberStage } from "./timelineMemory.js";
import { TIMELINE_TEXT } from "./timelineTexts.js";
import { EFFORT_TEXT } from "./effortTexts.js";
import { tageZwischen } from "./ticketFormat.js";
import { SYMBOL_PLUS } from "./ticketSymbole.js";
import type { TicketsClient } from "./ticketsClient.js";
import { DueSourceChips } from "../termine/DueSourceChips.js";
import { chipSources, requestSources, requestedItems } from "../termine/dueItems.js";
import type { DueSource } from "../termine/dueItems.js";
import { DUE_ITEM_TEXT } from "../termine/dueItemTexts.js";
import "./TicketTimeline.css";

/**
 * DIE ANSICHT ZEITACHSE (Spec Plan-Ansicht S1–S4, S12–S18, S20; Plan P3, P8, P10, P11) — bindet Werkzeugleiste, Laden,
 * Takt, Stufen, Blättern, Legende, Auf- und Zuklappen zusammen; Zeilen und Balken rechnet `timelineModel.ts`, die
 * Zeilenspalte zeichnet `TicketTimelineRows`, die Flächen `TicketTimelineChart`.
 *
 * - Laden wie das Board (F20): `getTicketBoard` mit dem Filter des Bereichs (S1, S16), beim ersten Mal parallel die Zeilen
 *   (`getTicketTimelineRows`, S2); beim Öffnen, bei Fensterfokus, alle 60 s und nach Mitternacht, nicht während einer
 *   Geste auf dem Rollfeld. Nur die jüngste Antwort zeichnet; ein Fehler lässt den alten Stand stehen.
 * - Das Fenster ist Zustand der Ansicht (P3) und geht an alle Flächen: Stufe (gemerkt je Benutzer, S17) um den Anker,
 *   Blättern um die halbe Spanne und Ziehen (der Anker wandert um den Versatz mit), „Heute“ (auch Taste 0 in der Gruppe
 *   „Zeitraum“) legt die gedrückte Stufe um heute, Pinch und „Zeitraum aufziehen“ setzen ein freies Fenster („frei“,
 *   Platz reserviert). Ein Stufenwechsel verliert „heute“ nie, wenn es im Fenster stand. Gedrückt ist immer die
 *   nächstliegende Stufe — die Leiste kippt nie.
 * - Auswahl (S15): die Zeilenspalte wählt, die Ansicht merkt Zeile und Ticket und sucht dessen Platz je Stand und
 *   Fenster neu; die Flächen zeigen Rahmen und Popup am Balken. Öffnet die Ansicht ein Ticket (Enter, Klick), schweigt
 *   das Popup bis zur nächsten neuen Wahl (Fixrunde 5, H1); Auswahl und Rahmen bleiben (Gesamt-Review K1).
 * - Aufklappen (S4, S13): `listFocusCards` je Eltern-Balken, die Zeile bleibt im Bild (`keepInView`); aufgeklappte
 *   Kennungen überstehen das Neuladen, ihre Karten kommen mit jedem neuen Stand frisch.
 * - Ein Rollfeld `.tl__koerper` über alle Zustandswechsel (Scrollposition bleibt), Flächen werden per `setOption`
 *   aktualisiert, nie neu eingehängt; die Kopfachse klebt oben, die Gruppenköpfe darunter, die Flächen stehen um
 *   HEADER_PX tiefer. Zugeklappte Gruppen und Gruppen ohne Zeilen haben keine Fläche.
 * - Gruppe „Zeitraum“ (Ruling 548): eine eigene Zeile unter der Leiste für alle Breiten — Stufen, „frei“, ◂ „Heute“ ▸,
 *   Fenster und „Zeitraum aufziehen“; neben der Legende, wenn die Breite reicht, sonst unter ihr, beide rechtsbündig.
 *   Alles hat feste Breiten: ob die Zeile umbricht, hängt nur an der Breite, nie an einer Auswahl.
 * - Telefon (S18, P11): Legende weg, Zeilenspalte 48 px; die Zeile „Zeitraum“ bleibt (Blättern ist die Hauptbedienung),
 *   ohne „Zeitraum aufziehen“ (Pinch deckt es ab, Dirk 549) und zweizeilig: Stufen und „frei“, darunter ◂ „Heute“ ▸ Fenster.
 * - Terminebene (Spec D22, D24, D26; Plan T19): die Quellen-Chips (ohne „Tickets“) als eigene Zeile direkt unter der
 *   Leiste; die Wahl hält der Bereich. Mit mindestens einer zugeschalteten Quelle lädt `useDueItems` die Termine (Fenster
 *   höchstens 400 Tage mit Hinweis, Unternehmen und Kunde des Filters — Team, Zuständig und „nur verknüpfte“ wirken nur
 *   auf Tickets, D11); neu mit dem Board (Fokus, Takt, Mitternacht), sofort bei neuer Quellenwahl, neuem Filter oder
 *   `neuLaden`, nach einer Fensteränderung erst 300 ms nach dem Ende der Geste. Gruppenfolge „Meilensteine“, Teams,
 *   Quellen; gezeichnet wird nur, was angefragt ist; ein Eintrag am Tag seines Fälligkeitstickets tritt hinter die Karte
 *   zurück, wenn sie im Board-Stand ist (D26 mit H2). Die Termingruppen entstehen unabhängig von der Klappstellung, und
 *   ihre Layouts hängen an der Gruppe: Auf- und Zuklappen zeichnet die übrigen Flächen nicht neu (Fixrunde G4).
 */

export type TicketTimelineProps = {
  client: TicketsClient; companies: readonly Unternehmen[]; ich: { id: string; name: string; istAdmin?: boolean };
  onOeffnen: (id: string) => void; onAnlegen: (companyId: string | null) => void; neuLaden?: number;
  /** Wie die Liste: Filter vom Bereich, Änderung als Updater auf den jüngsten Stand. */
  filter: BoardFilter; onFilter: (aendern: (alt: BoardFilter) => BoardFilter) => void;
  /** Wie das Board (F129): offene Tickets des Stands (mit Filtern) und der Tag des Servers — der Bereich zeigt das Datum. */
  onOffen?: (offen: number, heute: string) => void;
  /** „+n“ → Liste mit Filter Zuständig = Person (Bereich wechselt die Ansicht). */
  onZurListe: (filter: BoardFilter) => void;
  /** Terminebene (D22): die ganze Quellenwahl des Bereichs (gemerkt); Chips melden die neue Wahl. */
  dueSources: readonly DueSource[]; onDueSources: (s: DueSource[]) => void;
  /** Klick oder Enter an einer Marke: Meilenstein → Panel, sonst Sprung (T20) — der Bereich entscheidet. */
  onOpenDue: (item: DueItem) => void;
  /** Lieferantenquellen nur mit Lieferantensicht (D12). */
  supplierView: boolean;
  /** „+ Meilenstein“ — der eine Knopf in der Quellenzeile (Ruling D24, Aufgabe 8), sobald „Meilensteine“ angefragt ist:
   *  mit dem Unternehmen des Filters, bei „Alle“ `null`. Im Gruppenkopf steht keiner. */
  onCreateMilestone?: (companyId: string | null) => void;
};

/** Das Fenster und sein Anker (S14): Stufenwahl legt das Fenster der Stufe um den Anker. */
type View = { window: TimelineWindow; anchor: string };
/** Die Auswahl der Ansicht (Fixrunde 1, M2): Zeile und TICKET bzw. Eintrag (Schlüssel der Marke, D24). Der Platz in
 *  `row.bars` bzw. `row.markers` wird je Stand und Fenster neu gesucht; liegt er nicht (mehr) im Fenster, bleibt die
 *  Zeile gewählt (`barIndex: null`). */
type Choice = { rowId: string; itemId: string | null };
type Expanded = ReadonlyMap<string, readonly TicketBoardCard[]>;

const TAKT_MS = 60_000;
/** Die Termingruppen entstehen ohne Klappstellung; sie kommt danach dazu (Fixrunde G4). */
const NONE_COLLAPSED: ReadonlySet<string> = new Set();
const ROW_ITEMS = ".tl__zeilen > li[data-ticket-ids]";
const contains = (w: TimelineWindow, day: string): boolean => w.from <= day && day <= w.to;
const spanOf = (w: TimelineWindow): number => tageZwischen(w.from, w.to);
/** Gruppe „Zeitraum“ (Leiste bzw. Telefonzeile): dort setzt die Taste 0 auf heute (S14). */
const ZEITRAUM = "tl__zeitraum";

/** Die erste Zeile (in Seitenfolge), die den Balken des Tickets trägt, sonst null. */
function rowOf(root: ParentNode, ticketId: string): HTMLElement | null {
  return [...root.querySelectorAll<HTMLElement>(ROW_ITEMS)]
    .find((li) => (li.dataset.ticketIds ?? "").split(" ").includes(ticketId)) ?? null;
}

/** Fokus-Rückgabe nach dem Dialog (Aufgabe 7, Muster F121): Fokus auf die Zeile des Tickets, ohne zu rollen. */
export function focusTimelineRow(root: ParentNode, ticketId: string): boolean {
  const li = rowOf(root, ticketId);
  li?.focus({ preventScroll: true });
  return li !== null;
}

export function TicketTimeline(p: TicketTimelineProps): ReactElement {
  const phone = useTelefon();
  const thema = useDiagrammThema();
  const [stand, setStand] = useState<{ board: TicketBoard; nowMs: number } | null>(null);
  const [rows, setRows] = useState<TimelineRowsData | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [view, setView] = useState<View | null>(null);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => readCollapsed(p.ich.id));
  const [expanded, setExpanded] = useState<Expanded>(() => new Map());
  const [choice, setChoice] = useState<Choice | null>(null);
  // Fixrunde 5, H1: Öffnet die Ansicht ein Ticket, schweigt das Auswahl-Popup der Flächen (es läge sonst unter dem Dialog
  // weiter offen); die Auswahl selbst bleibt für die Zeilenspalte, erst eine neue Wahl zeigt es wieder (Fixrunde 1, G5).
  const [tipHidden, setTipHidden] = useState(false);
  const [frameMode, setFrameMode] = useState(false);
  const sektion = useRef<HTMLElement | null>(null);
  const live = useRef<HTMLDivElement | null>(null);
  const geste = useRef(false);
  const lauf = useRef(0);
  // Fixrunde 1, G3: nach dem Aushängen setzt keine späte Antwort mehr Zustand (StrictMode hängt neu ein: wieder lebendig).
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; lauf.current += 1; };
  }, []);

  // Die Anfrage aus den Filterwerten — ein vom Bereich neu gebautes, gleiches Objekt lädt nicht neu (wie die Liste).
  const { company, customer, assignee, linked } = p.filter;
  const team = p.filter.team ?? "";
  const anfrage = useMemo(() => filterAnfrage({ company, customer, assignee, linked, team }),
    [company, customer, assignee, linked, team]);

  // Nur die jüngste Anfrage zeichnet. Aufgeklappte Eltern laden ihre Kinder mit dem neuen Stand nach (S4); ein Kind-Abruf,
  // der scheitert, behält die alten Karten.
  const latest = useRef({ rows, expanded, phone });
  latest.current = { rows, expanded, phone };
  /** `withRows`: auch die Zeilen neu (Fensterfokus, Mitternacht) — Teams und Mitglieder ändern sich selten, aber eine
   *  neue Mitgliedschaft brächte sonst private Tickets ohne ihre Gruppe (Gesamt-Review K4). */
  const laden = useCallback(async (withRows = false) => {
    const nummer = ++lauf.current;
    const offen = [...latest.current.expanded.keys()];
    try {
      const [board, zeilen, kinder] = await Promise.all([
        p.client.getTicketBoard(anfrage),
        (withRows ? null : latest.current.rows) ?? p.client.getTicketTimelineRows(),
        Promise.all(offen.map((id) => p.client.listFocusCards(id).then((r) => [id, r.items] as const, () => null))),
      ]);
      if (nummer !== lauf.current) return;
      const frisch = new Map(kinder.filter((k) => k !== null));
      setStand({ board, nowMs: nowOnAxis(board.heute, new Date()) });
      setRows(zeilen);
      setView((v) => v ?? { window: windowFor(readStage(p.ich.id, latest.current.phone), board.heute), anchor: board.heute });
      if (frisch.size > 0) setExpanded((alt) => new Map([...alt].map(([id, items]) => [id, frisch.get(id) ?? items])));
      setFehler(null);
    } catch {
      if (nummer === lauf.current) setFehler(TIMELINE_TEXT.loadError);
    }
  }, [p.client, anfrage, p.ich.id]);

  // ── Terminebene (D22, D24) ─────────────────────────────────────────────────────────────────────────────────
  const quellen = requestSources(p.dueSources, "TIMELINE", p.supplierView);
  const termine = useDueItems({ client: p.client, sources: quellen, window: view?.window ?? null,
    company: company === "ALLE" ? null : company, customer: customer === "" ? null : customer, reload: p.neuLaden,
    gesture: geste });
  const { reloadNow: ladeTermine, gestureEnded } = termine;
  const gestureEndedRef = useRef(gestureEnded);
  gestureEndedRef.current = gestureEnded;

  useEffect(() => { void laden(); }, [laden, p.neuLaden]);
  const heute = stand?.board.heute ?? null;
  useEffect(() => {
    const neu = () => { if (!geste.current) { void laden(); ladeTermine(); } };
    const ganz = () => { if (!geste.current) { void laden(true); ladeTermine(); } };
    const takt = setInterval(neu, TAKT_MS);
    const mitternacht = heute === null ? undefined : setTimeout(ganz, msBisMitternachtBerlin(new Date()) + 1000);
    window.addEventListener("focus", ganz);
    return () => { clearInterval(takt); clearTimeout(mitternacht); window.removeEventListener("focus", ganz); };
  }, [laden, ladeTermine, heute]);
  // Die Geste beginnt auf dem Rollfeld (onPointerDown) und endet, wo der Zeiger losgelassen wird — in der Capture-Phase,
  // damit kein Element das Ende verschluckt; verliert das Fenster den Fokus (iframe, anderes Programm), endet sie auch (G4).
  // Wartet ein Neuladen der Termine auf das Ende, beginnen jetzt seine 300 ms (D24).
  useEffect(() => {
    const end = () => { geste.current = false; gestureEndedRef.current(); };
    window.addEventListener("pointerup", end, true);
    window.addEventListener("pointercancel", end, true);
    window.addEventListener("blur", end);
    return () => {
      window.removeEventListener("pointerup", end, true);
      window.removeEventListener("pointercancel", end, true);
      window.removeEventListener("blur", end);
    };
  }, []);

  // F129: der Zähler am Reiter „Board“ und das Datum — über eine Ref, ein neuer Rückruf allein meldet nicht noch einmal.
  const offeneAnzahl = (stand?.board.tickets ?? []).filter((t) => t.stateKind !== "DONE").length;
  const onOffenRef = useRef(p.onOffen);
  onOffenRef.current = p.onOffen;
  useEffect(() => { if (heute !== null) onOffenRef.current?.(offeneAnzahl, heute); }, [offeneAnzahl, heute]);

  const win = view?.window ?? null;
  const timeline = useMemo(() => (stand === null || rows === null || win === null ? null : buildTimeline({
    cards: stand.board.tickets, rows, heute: stand.board.heute, meId: p.ich.id, window: win, collapsed, expanded, phone,
    filter: { team, assignee } })), [stand, rows, win, collapsed, expanded, phone, p.ich.id, team, assignee]);
  // D24, D26: Marken nur aus den angefragten Quellen (eine abgeschaltete verschwindet sofort, nicht erst mit der Antwort);
  // ein Eintrag tritt nur hinter eine Karte des Board-Stands zurück (H2).
  const due = termine.due;
  const quellenKey = quellen.join(",");
  const visibleItems = useMemo(() => (due === null || quellenKey === "" ? null : requestedItems(due.items, quellenKey.split(","))),
    [due, quellenKey]);
  const boardIds = useMemo(() => new Set((stand?.board.tickets ?? []).map((t) => t.id)), [stand]);
  const firmenKey = p.companies.map((c) => `${c.id}:${c.shortCode}`).join(",");
  const firmen = useMemo(() => p.companies.map((c) => ({ companyId: c.id, shortCode: c.shortCode })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- neu nur mit anderen Unternehmen, nicht mit neuer Liste
    [firmenKey]);
  // G4: Der Bau hängt nicht an der Klappstellung; die Stellung kommt danach dazu, nur geänderte Gruppen werden neu.
  const dueBuilt = useMemo(() => (visibleItems === null || stand === null || win === null ? null : buildDueGroups({
    items: visibleItems, window: win, heute: stand.board.heute, collapsed: NONE_COLLAPSED, boardTicketIds: boardIds,
    companies: firmen })), [visibleItems, stand, win, boardIds, firmen]);
  const dueGroups = useMemo(() => {
    if (dueBuilt === null) return null;
    const klapp = (g: TimelineGroup): TimelineGroup => (g.collapsed === collapsed.has(g.id) ? g : { ...g, collapsed: !g.collapsed });
    return { ...dueBuilt, milestones: dueBuilt.milestones === null ? null : klapp(dueBuilt.milestones),
             sources: dueBuilt.sources.map(klapp) };
  }, [dueBuilt, collapsed]);
  const groups = useMemo(() => {
    const milestones = dueGroups?.milestones ?? null;
    return timeline === null ? [] : [...(milestones === null ? [] : [milestones]), ...timeline.groups, ...(dueGroups?.sources ?? [])];
  }, [timeline, dueGroups]);
  const dueByKey = useMemo(() => new Map((due?.items ?? []).map((i) => [i.key, i])), [due]);
  // Das Layout hängt an der Gruppe: unveränderte Gruppen behalten ihres, ihre Flächen bekommen keine neue Option (G4).
  const layoutCache = useRef(new WeakMap<TimelineGroup, ReturnType<typeof layout>>());
  const layouts = useMemo(() => groups.map((g) => {
    const cached = layoutCache.current.get(g);
    if (cached !== undefined) return cached;
    const l = layout(g);
    layoutCache.current.set(g, l);
    return l;
  }), [groups]);
  const kalender = stand?.board.kalender;
  const companyColors = useMemo(() => new Map((kalender ?? []).map((k) => [k.companyId, k.colorHex])), [kalender]);
  const teamNames = useMemo(() => new Map((rows?.teams ?? []).map((t) => [t.id, t.name])), [rows]);
  const rowById = useMemo(() => new Map(groups.flatMap((g) => g.rows).map((r) => [r.id, r])), [groups]);
  /** Die Halte einer Zeile: Balken bzw. Marken (D24). */
  const stopsOf = (id: string): readonly { id: string }[] => {
    const row = rowById.get(id);
    return row === undefined ? [] : row.kind === "DUE" ? row.markers : row.bars;
  };
  // M2: der Platz des gewählten Tickets bzw. Eintrags in seiner Zeile, je Stand und Fenster neu gesucht.
  const selection = useMemo<TimelineSelection | null>(() => {
    if (choice === null) return null;
    const at = choice.itemId === null ? -1 : stopsOf(choice.rowId).findIndex((b) => b.id === choice.itemId);
    return { rowId: choice.rowId, barIndex: at >= 0 ? at : null };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stopsOf liest nur rowById
  }, [choice, rowById]);

  // ── Fenster ──────────────────────────────────────────────────────────────────────────────────────────────────
  /** Stufe um den Anker; stand „heute“ im Fenster und fiele es hinaus, kommt die Stufe um heute (Fixrunde 1, M1). */
  const chooseStage = (s: TimelineStage) => {
    rememberStage(p.ich.id, s);
    setView((v) => {
      if (v === null) return v;
      const keepToday = heute !== null && contains(v.window, heute) && !contains(windowFor(s, v.anchor), heute);
      const anchor = keepToday ? heute : v.anchor;
      return { window: windowFor(s, anchor), anchor };
    });
  };
  const page = (direction: -1 | 1) => setView((v) => {
    if (v === null) return v;
    const w = shiftWindow(v.window, direction);
    return { window: w, anchor: addDays(v.anchor, tageZwischen(v.window.from, w.from)) };
  });
  const today = () => { if (heute !== null) setView((v) => v && { window: windowFor(nearestStage(v.window), heute), anchor: heute }); };
  /** Geste oder Rahmen (P3, Fixrunde 1 M1): Ziehen behält die Spanne und verschiebt den Anker um den Versatz (wie
   *  Blättern); Pinch und Rahmen ändern sie — der Anker bleibt, wenn er im neuen Fenster liegt, sonst in dessen Mitte.
   *  Der Rahmen gilt für ein Fenster. */
  const freeWindow = useCallback((w: TimelineWindow) => {
    setView((v) => ({ window: w, anchor: v !== null && spanOf(v.window) === spanOf(w)
      ? addDays(v.anchor, tageZwischen(v.window.from, w.from))
      : v !== null && contains(w, v.anchor) ? v.anchor : addDays(w.from, Math.floor(spanOf(w) / 2)) }));
    setFrameMode(false);
  }, []);
  const todayRef = useRef(today);
  todayRef.current = today;
  useEffect(() => {
    const el = sektion.current;
    if (el === null) return undefined;
    const taste = (e: KeyboardEvent) => {
      if (e.key !== "0" || e.altKey || e.ctrlKey || e.metaKey || e.defaultPrevented) return;
      if (!e.composedPath().some((n) => n instanceof Element && n.classList.contains(ZEITRAUM))) return;
      e.preventDefault();
      todayRef.current();
    };
    el.addEventListener("keydown", taste);
    return () => el.removeEventListener("keydown", taste);
  }, []);

  // ── Auf- und Zuklappen ───────────────────────────────────────────────────────────────────────────────────────
  // S15: die geklickte Zeile bleibt an ihrem Platz im Fenster (Muster `halten` im Board).
  const halten = useRef<(() => void) | null>(null);
  useLayoutEffect(() => { halten.current?.(); halten.current = null; }, [expanded]);
  /** Die Zeile, die stehen bleibt: die fokussierte (Leertaste), sonst die erste mit dem Ticket in der Gruppe der Fläche —
   *  eine Wiederholung (533) in einer späteren Gruppe hält ihre eigene Zeile (G2). */
  const hold = (ticketId: string, groupId: string) => {
    const root = sektion.current;
    const scope = root === null ? null : root.querySelector(`.tl__zeilenspalte[data-group="${groupId}"]`) ?? root;
    const aktiv = document.activeElement;
    const li = aktiv instanceof HTMLElement && root?.contains(aktiv) === true && aktiv.matches(ROW_ITEMS)
      && (aktiv.dataset.ticketIds ?? "").split(" ").includes(ticketId) ? aktiv : scope === null ? null : rowOf(scope, ticketId);
    halten.current = li === null ? null : keepInView(li);
  };
  const laeuft = useRef(new Set<string>());
  const toggleBar = async (ticketId: string, groupId: string) => {
    if (latest.current.expanded.has(ticketId)) {
      hold(ticketId, groupId);
      setExpanded((alt) => new Map([...alt].filter(([id]) => id !== ticketId)));
      return;
    }
    if (laeuft.current.has(ticketId)) return;                   // ein Abruf je Eltern-Balken
    laeuft.current.add(ticketId);
    try {
      const r = await p.client.listFocusCards(ticketId);
      if (!alive.current) return;
      hold(ticketId, groupId);
      setExpanded((alt) => new Map(alt).set(ticketId, r.items));
    } catch {
      if (alive.current) setFehler(TIMELINE_TEXT.loadError);
    } finally {
      laeuft.current.delete(ticketId);
    }
  };
  /** Klappstellung (S17, Review 4 H1): zu = `team:<id>`, ausdrücklich offen = `!team:<id>` — so bleibt auch eine Gruppe
   *  offen, die das Modell ab der 9. Gruppe oder ab 300 Zeilen zuklappt. */
  const toggleGroup = (groupId: string) => {
    const close = groups.find((g) => g.id === groupId)?.collapsed === false;
    setCollapsed((alt) => {
      const neu = new Set(alt);
      neu.delete(close ? `!${groupId}` : groupId);
      neu.add(close ? groupId : `!${groupId}`);
      rememberCollapsed(p.ich.id, neu);
      return neu;
    });
  };

  const { onFilter } = p;
  const filterSetzen = useCallback((f: Partial<BoardFilter>) => {
    onFilter((alt) => {
      const neu = { ...alt, ...f };
      merkeFilter(neu);
      return neu;
    });
  }, [onFilter]);
  const zurListe = (person: TimelinePerson) => p.onZurListe({ ...p.filter, assignee: person.id });
  /** Nutzerwahl der Zeilenspalte → Zeile und Ticket (M2); sie zeigt das Popup wieder. Weil der Platz je Stand neu gesucht
   *  wird, ist `barIndex` immer gültig — die Klemmung der Zeilenspalte (G5 aus Aufgabe 4) meldet nie, ein Neuladen bei
   *  offenem Dialog holt das Popup also nicht zurück (Fixrunde 1, G5). */
  const select = (s: TimelineSelection | null) => {
    setTipHidden(false);
    setChoice(s === null ? null
      : { rowId: s.rowId, itemId: s.barIndex === null ? null : stopsOf(s.rowId)[s.barIndex]?.id ?? null });
  };
  const open = (ticketId: string) => { setTipHidden(true); p.onOeffnen(ticketId); };
  const openDue = (item: DueItem) => { setTipHidden(true); p.onOpenDue(item); };
  const openMarker = (key: string) => { const item = dueByKey.get(key); if (item !== undefined) openDue(item); };
  const createMilestone = p.onCreateMilestone;

  // ── Leiste ───────────────────────────────────────────────────────────────────────────────────────────────────
  const stage = win === null ? null : nearestStage(win);
  const free = win !== null && stageOf(win) === null;
  const backPopup = useSofortPopup<HTMLElement>(TIMELINE_TEXT.back);
  const forwardPopup = useSofortPopup<HTMLElement>(TIMELINE_TEXT.forward);
  const raster = { "--tl-label": `${phone ? LABEL_PX_PHONE : LABEL_PX}px` } as CSSProperties;
  const axis = stand === null || win === null ? null
    : { window: win, stage: stageOf(win), heute: stand.board.heute, nowMs: stand.nowMs, kalender: stand.board.kalender, thema, phone };

  return (
    // tabIndex -1: Fokus-Ersatz nach dem Schließen des Panels (F129), wie Board und Liste.
    <section className="tl-zeitachse" aria-label={TIMELINE_TEXT.tab} ref={sektion} tabIndex={-1}
      {...(stage === null ? {} : { "data-stage": stage })} data-bars={timeline?.bars ?? 0} data-markers={dueGroups?.markers ?? 0}>
      <Werkzeugleiste className="tl__werkzeugleiste" aria-label={TIMELINE_TEXT.toolbarName} aktiv={anzahlAktiverFilter(p.filter)}
        onZurueckgesetzt={() => filterSetzen(LEERER_FILTER)}
        filter={(
          <TicketFilterleiste client={p.client} companies={p.companies} ich={p.ich} tickets={stand?.board.tickets ?? []}
            filter={p.filter} onFilter={filterSetzen} />
        )}
        haupt={(
          <Knopf art="primaer" symbol={SYMBOL_PLUS} onClick={() => p.onAnlegen(company === "ALLE" ? null : company)}>
            {TIMELINE_TEXT.newTask}
          </Knopf>
        )} />
      {/* D22: die Quellen-Chips als eigene Zeile direkt unter der Leiste (ohne „Tickets“ — Tickets sind die Balken).
          Ruling zu D24 (Aufgabe 8): „+ Meilenstein“ steht dahinter, sobald „Meilensteine“ angefragt ist, auch ohne Treffer;
          sonst verborgen mit Platz (die Zeile kippt nicht mit der Quellenwahl). */}
      <div className="tl__quellen">
        <DueSourceChips sources={chipSources("TIMELINE", p.supplierView)} value={p.dueSources} onChange={p.onDueSources} />
        {createMilestone !== undefined && (
          <span className="tl__milestone-create" hidden={!quellen.includes("MILESTONE")}>
            <KiKnopf kompakt onClick={() => createMilestone(company === "ALLE" ? null : company)}>{TIMELINE_TEXT.newMilestone}</KiKnopf>
          </span>
        )}
      </div>
      {/* Ruling 548: unter der Leiste die Gruppe „Zeitraum“ als eigene Zeile für alle Breiten (die Leiste legte sie bei
          1280/1440 px ins „…“), daneben bzw. darüber die Legende oben rechts (S12); dann Hinweise und das Rollfeld. */}
      <div className="tl__steuerzeile">
        <div className={ZEITRAUM} role="group" aria-label={TIMELINE_TEXT.stageGroup}>
          <Optionen aria-label={TIMELINE_TEXT.stageChoice} darstellung="segmente" richtung="waagerecht" wert={stage ?? ""}
            onGeaendert={(d) => { const s = TIMELINE_STAGES.find((x) => x === d.wert); if (s !== undefined) chooseStage(s); }}>
            {TIMELINE_STAGES.map((s) => <option key={s} value={s}>{TIMELINE_TEXT.stages[s]}</option>)}
          </Optionen>
          {/* „frei“ hat immer seinen Platz (`visibility`), sichtbar nur bei einem freien Fenster (S14). */}
          <span className="tl__frei" hidden={!free}><Chip>{TIMELINE_TEXT.free}</Chip></span>
          {/* 549: am Telefon zweizeilig — Stufen und „frei“, darunter Blättern und Fenster. */}
          {phone && <span className="tl__break" aria-hidden="true" />}
          <KiKnopf kompakt name={TIMELINE_TEXT.back} knopfRef={backPopup} onClick={() => page(-1)}>
            <Icon name="chevron-left" size={12} />
          </KiKnopf>
          <KiKnopf kompakt onClick={today}>{TIMELINE_TEXT.today}</KiKnopf>
          <KiKnopf kompakt name={TIMELINE_TEXT.forward} knopfRef={forwardPopup} onClick={() => page(1)}>
            <Icon name="chevron-right" size={12} />
          </KiKnopf>
          <span className="tl__fenster" aria-live="polite">{win === null ? "" : TIMELINE_TEXT.windowText(win.from, win.to)}</span>
          {!phone && (
            <Knopf art="sekundaer" gestalt="pille" gedrueckt={frameMode} onClick={() => setFrameMode((f) => !f)}>
              {TIMELINE_TEXT.frame}
            </Knopf>
          )}
        </div>
        <Legend hidden={phone} />
      </div>
      {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
      {termine.error && <Hinweis art="fehler">{DUE_ITEM_TEXT.loadError}</Hinweis>}
      {timeline?.truncated === true && <Hinweis art="warnung">{TIMELINE_TEXT.limit(stand?.board.tickets.length ?? 0)}</Hinweis>}
      {/* Review Focus 4: ein Fenster über 400 Tage lädt die ersten 400 und sagt es — kein Fehler. */}
      {termine.clipped && <Hinweis art="info">{TIMELINE_TEXT.dueWindowLimit}</Hinweis>}
      {due !== null && (dueGroups?.truncated === true || due.truncated) && (
        <Hinweis art="warnung">{TIMELINE_TEXT.dueLimit(due.total)}</Hinweis>
      )}
      <div className="tl__koerper" style={raster} onPointerDown={() => { geste.current = true; }}>
        {timeline !== null && axis !== null && <>
          <div className="tl__achsenzeile">
            <span aria-hidden="true" />
            <TicketTimelineChart kind="axis" {...axis} onWindow={freeWindow} />
          </div>
          {groups.map((g, i) => {
            const l = layouts[i]!;
            return (
              <div key={g.id} className="tl__gruppe">
                <TicketTimelineRows group={g} layout={l} phone={phone} heute={axis.heute} selection={selection}
                  onSelect={select} onOpen={open} onToggleBar={(id) => { void toggleBar(id, g.id); }}
                  onToggleGroup={toggleGroup} onMore={zurListe} liveRef={live} groupNames={teamNames} meId={p.ich.id}
                  onOpenDue={openDue} companies={p.companies} />
                <div className="tl__flaechen" style={{ paddingTop: HEADER_PX }}>
                  {!g.collapsed && l.blocks.map((block, k) => (
                    <TicketTimelineChart key={k} kind="body" {...axis} group={g} layout={l} block={block}
                      selection={selection} selectionTip={!tipHidden}
                      companyColors={companyColors} teamNames={teamNames} frameMode={frameMode} onWindow={freeWindow}
                      onBarClick={open} onToggle={(id) => { void toggleBar(id, g.id); }} onMarkerClick={openMarker} />
                  ))}
                </div>
              </div>
            );
          })}
        </>}
      </div>
      <div className="tl__live" aria-live="polite" ref={live} />
    </section>
  );
}

/** S12: Legende oben rechts — fünf Zustände als rahmenlose Kennzeichen in der Folge der Skizze, dazu die Musterproben
 *  „offenes Ende“, „Vorlauf“, „auch in anderer Gruppe“, „Ist/Plan“, „über Plan“ (A22; Streifen und Verläufe aus
 *  Tokens, P6) und „Meilenstein“ (Raute), „Fälligkeit“ (Punkt) der Terminebene (D24) — immer, auch ohne zugeschaltete
 *  Quelle: die Steuerzeile kippt nicht mit der Quellenwahl; unter 744 px weg. */
function Legend(p: { hidden: boolean }): ReactElement {
  const s = TIMELINE_TEXT.states;
  return (
    <div className="tl__legende" role="group" aria-label={TIMELINE_TEXT.legendName} hidden={p.hidden}>
      <Chip statusArt="ok">{s.OPEN}</Chip>
      <Chip className="tl__wartend">{s.WAITING}</Chip>
      <Chip>{s.DONE}</Chip>
      <Chip statusArt="fehler">{s.OVERDUE}</Chip>
      <Chip statusArt="warnung">{s.DUE_TODAY}</Chip>
      {([["ende", TIMELINE_TEXT.legendOpenEnd], ["vorlauf", TIMELINE_TEXT.legendLead], ["doppelt", TIMELINE_TEXT.legendDuplicate],
         ["anteil", EFFORT_TEXT.legendShare], ["ueber", EFFORT_TEXT.legendOverrun], ["raute", TIMELINE_TEXT.legendMilestone],
         ["punkt", TIMELINE_TEXT.legendDue]] as const)
        .map(([art, text]) => (
          <span key={art} className="tl__probe"><span className={`tl__muster tl__muster--${art}`} aria-hidden="true" />{text}</span>
        ))}
    </div>
  );
}
